/**
 * OAuth API Module
 * Методы для OAuth авторизации через Google, VK и другие провайдеры
 * 
 * @typedef {Object} OAuthExchangeRequest
 * @property {string} code - Код авторизации от провайдера
 * @property {string} [state] - State параметр для CSRF защиты
 * @property {string} [provider] - ID провайдера (google, vk)
 * @property {string} redirectUri - URI для редиректа
 * 
 * @typedef {Object} OAuthTokenResponse
 * @property {string} accessToken - JWT access token
 * @property {string} refreshToken - Refresh token
 * @property {number} expiresIn - Время жизни токена в секундах
 * @property {string} tokenType - Тип токена (Bearer)
 */

/**
 * Базовый URL для Auth API
 * @returns {string}
 */
function getBaseUrl() {
  return import.meta.env.VITE_AUTH_API_URL ?? '';
}

/**
 * POST /api/auth/oauth/callback
 * Обмен authorization code на токены
 * @param {OAuthExchangeRequest} data
 * @returns {Promise<OAuthTokenResponse>}
 */
export async function exchangeCode(data) {
  const response = await fetch(`${getBaseUrl()}/api/auth/oauth/callback`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    const error = new Error(errorData.message || 'OAuth exchange failed');
    error.status = response.status;
    error.data = errorData;
    throw error;
  }

  return response.json();
}

/**
 * GET /api/auth/oauth/{provider}
 * Получить URL для редиректа на OAuth провайдер
 * Обычно используется напрямую через window.location.href
 * @param {string} provider - ID провайдера (google, vk)
 * @param {string} redirectUri - URI для callback
 * @returns {string} URL для редиректа
 */
export function getOAuthRedirectUrl(provider, redirectUri) {
  const baseUrl = getBaseUrl();
  const encodedRedirect = encodeURIComponent(redirectUri);
  return `${baseUrl}/api/auth/oauth/${provider}?redirect_uri=${encodedRedirect}`;
}

/**
 * POST /api/auth/oauth/link
 * Привязать OAuth аккаунт к существующему пользователю (требует авторизации)
 * @param {{ provider: string, code: string, redirectUri: string }} data
 * @returns {Promise<{ success: boolean, provider: string }>}
 */
export async function linkOAuthAccount(data) {
  const token = localStorage.getItem('access_token');
  
  const response = await fetch(`${getBaseUrl()}/api/auth/oauth/link`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    const error = new Error(errorData.message || 'Failed to link OAuth account');
    error.status = response.status;
    error.data = errorData;
    throw error;
  }

  return response.json();
}

/**
 * DELETE /api/auth/oauth/unlink/{provider}
 * Отвязать OAuth аккаунт от пользователя (требует авторизации)
 * @param {string} provider - ID провайдера (google, vk)
 * @returns {Promise<{ success: boolean }>}
 */
export async function unlinkOAuthAccount(provider) {
  const token = localStorage.getItem('access_token');
  
  const response = await fetch(`${getBaseUrl()}/api/auth/oauth/unlink/${provider}`, {
    method: 'DELETE',
    headers: {
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    const error = new Error(errorData.message || 'Failed to unlink OAuth account');
    error.status = response.status;
    error.data = errorData;
    throw error;
  }

  return response.json();
}

/**
 * GET /api/auth/oauth/providers
 * Получить список доступных OAuth провайдеров
 * @returns {Promise<Array<{ id: string, name: string, enabled: boolean }>>}
 */
export async function getAvailableProviders() {
  const response = await fetch(`${getBaseUrl()}/api/auth/oauth/providers`);

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    const error = new Error(errorData.message || 'Failed to get OAuth providers');
    error.status = response.status;
    error.data = errorData;
    throw error;
  }

  return response.json();
}
